import { useEffect, useRef, useState } from "react";
import {
  BanknoteIcon,
  BotIcon,
  BriefcaseBusinessIcon,
  CheckIcon,
  EyeIcon,
  InfoIcon,
  KeyRoundIcon,
  ListFilterIcon,
  LoaderCircleIcon,
  NotebookPenIcon,
  SearchIcon,
  UserRoundXIcon,
  XIcon,
} from "lucide-react";

import { Column, Row, Text } from "@/components";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";

import { useClassifyKeywordColors } from "@/hooks/mutations/useClassifyKeywordColors";
import { useGetJob } from "@/hooks/queries/useGetJob";
import { useGetSallyCandidates } from "@/hooks/queries/useGetSallyCandidates";
import { useCurrentCompanyId } from "@/hooks/useCurrentCompanyId";
import { useCurrentJobId } from "@/hooks/useCurrentJobId";
import { MAX_SEARCH_V2_KEYWORDS } from "@/lib/constants";
import { cn } from "@/lib/utils";
import { searchStore } from "@/stores/search";
import {
  ClassifiedKeyword,
  PositionCategory,
  SallyState,
  SearchV2Filters,
  SearchV2TenureFilter,
} from "@/types";
import { TenureFilter } from "./filters/TenureFilter";

const emptyFilters: SearchV2Filters = {
  includePastSubmitted: false,
  includeRejected: false,
  keywords: [],
  payCeiling: "",
  tenure: null,
  title: [],
};

// A category alone doesn't filter anything — tenure only counts once a bound
// is set. The draft always carries a category so the picker has a selection.
const hasTenureRange = (tenure?: SearchV2TenureFilter | null) =>
  Boolean(tenure) &&
  (tenure?.floor !== undefined || tenure?.ceiling !== undefined);

const getKeywordInput = (keywords: ClassifiedKeyword[]) => {
  return keywords.map(({ keyword }) => keyword).join(", ");
};

// Sally mode's corpus is fixed and only keywords narrow within it, so the other
// filters would be inert there — it gets its own, shorter section list.
const KEYWORDS_SECTION = {
  description:
    "Foundational terms that should appear in a candidate's work experience.",
  icon: KeyRoundIcon,
  key: "keywords",
  label: "Keywords",
  placeholder: "e.g. PLC",
} as const;

const SALLY_STATES_SECTION = {
  description: "Limit to where each candidate landed with Sally on this job.",
  icon: BotIcon,
  key: "sallyStates",
  label: "Sally status",
  placeholder: "",
} as const;

// Ordered by what a staffer would do about it; the first four are the second
// attempts worth making, which is why they're the default selection.
const SALLY_STATE_OPTIONS: { label: string; state: SallyState }[] = [
  { label: "Held off", state: SallyState.RULE_BLOCKED },
  { label: "No reply", state: SallyState.UNREACHABLE },
  { label: "Declined", state: SallyState.DECLINED },
  { label: "Bad contact", state: SallyState.BAD_CONTACT },
  { label: "In conversation", state: SallyState.IN_CONVERSATION },
  { label: "Queued", state: SallyState.PENDING_OUTREACH },
  { label: "Converted", state: SallyState.CONVERTED },
  { label: "Passed", state: SallyState.STAFFER_REJECTED },
  { label: "Job closed", state: SallyState.JOB_CLOSED },
  { label: "Opted out", state: SallyState.OPTED_OUT },
];

const FILTER_SECTIONS = [
  KEYWORDS_SECTION,
  {
    description:
      "Add current or previous job titles. Candidates matching any listed title are included.",
    icon: BriefcaseBusinessIcon,
    key: "title",
    label: "Title",
    placeholder: "e.g. Maintenance Technician",
  },
  {
    description:
      "Hide candidates whose current pay is above this hourly rate. Enter the job's rate, e.g. 30 to hide candidates seeking more than $30/hr. Candidates with no current pay on file are still shown.",
    icon: BanknoteIcon,
    key: "payCeiling",
    label: "Pay ceiling",
    placeholder: "e.g. 30",
  },
  {
    description: "Add relevant tenure options based on the position",
    icon: NotebookPenIcon,
    key: "tenure",
    label: "Tenure",
    placeholder: "e.g. 4",
  },
  {
    description:
      "Include candidates who were previously rejected for this job.",
    icon: UserRoundXIcon,
    key: "includeRejected",
    label: "Rejected",
    placeholder: "",
  },
  {
    description:
      "Include candidates who are past the submitted stage. They're hidden by default; when included they sort to the bottom of each page.",
    icon: BriefcaseBusinessIcon,
    key: "includePastSubmitted",
    label: "Past Submit Stage",
    placeholder: "",
  },
] as const;

const SALLY_FILTER_SECTIONS = [SALLY_STATES_SECTION, KEYWORDS_SECTION] as const;

type SearchV2Filter =
  | (typeof FILTER_SECTIONS)[number]["key"]
  | (typeof SALLY_STATES_SECTION)["key"];

interface SearchV2FilterSection {
  description: string;
  icon: typeof BriefcaseBusinessIcon;
  key: SearchV2Filter;
  label: string;
  placeholder: string;
}

export const SearchV2FiltersDialog = ({
  filters,
  hiddenUserCount,
  isSallyMode,
  onApply,
  onClearHiddenUsers,
  onSallyStatesChange,
  sallyStates,
}: {
  filters: SearchV2Filters;
  hiddenUserCount: number;
  isSallyMode: boolean;
  onApply: (filters: SearchV2Filters) => void;
  onClearHiddenUsers: () => void;
  onSallyStatesChange: (states: SallyState[]) => void;
  sallyStates: SallyState[];
}) => {
  /* Database CRUD Operations */
  // Read — shares the mode's query key, so this is the same cached fetch.
  const sally = useGetSallyCandidates({ enabled: isSallyMode });
  // Same key as the job page, so this is a cache hit; seeds the tenure category.
  const companyId = useCurrentCompanyId();
  const jobId = useCurrentJobId();
  const { data: job } = useGetJob({ companyId, jobId });

  /* React Hooks */
  const {
    isPending: isClassifyingKeywordColors,
    mutate: classifyKeywordColors,
  } = useClassifyKeywordColors();
  const [activeFilter, setActiveFilter] = useState<SearchV2Filter>("keywords");
  const [draftFilters, setDraftFilters] = useState(filters);
  const [draftSallyStates, setDraftSallyStates] = useState(sallyStates);
  const [filterSearchQuery, setFilterSearchQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  // Only the in-progress token — committed keywords live as chips in
  // draftFilters.keywords.
  const [keywordInput, setKeywordInput] = useState("");
  const [titleInput, setTitleInput] = useState(filters.title.join(", "));

  /* Helpers */
  const sections: SearchV2FilterSection[] = isSallyMode
    ? [...SALLY_FILTER_SECTIONS]
    : [...FILTER_SECTIONS];

  // Every state selected means nothing is being excluded, so it doesn't count
  // as an active filter — the same way an empty keyword box doesn't.
  const isSallyStatesFiltered = (states: SallyState[]) =>
    states.length !== SALLY_STATE_OPTIONS.length;

  const defaultTenure: SearchV2TenureFilter = {
    category: job?.positionCategory ?? PositionCategory.PRODUCTION,
  };
  const draftTenure = draftFilters.tenure ?? defaultTenure;

  const hasFilterValue = (filter: SearchV2Filter) => {
    if (filter === "sallyStates") {
      return isSallyStatesFiltered(draftSallyStates);
    }
    if (filter === "tenure") return hasTenureRange(draftFilters.tenure);
    const value = draftFilters[filter];
    return Array.isArray(value) ? value.length > 0 : Boolean(value);
  };

  const hasAppliedFilterValue = (filter: SearchV2Filter) => {
    if (filter === "sallyStates") {
      return isSallyStatesFiltered(sallyStates);
    }
    if (filter === "tenure") return hasTenureRange(filters.tenure);
    const value = filters[filter];
    return Array.isArray(value) ? value.length > 0 : Boolean(value);
  };

  const activeFilterCount = sections.filter((section) =>
    hasFilterValue(section.key),
  ).length;
  const appliedFilterCount = sections.filter((section) =>
    hasAppliedFilterValue(section.key),
  ).length;
  const activeSection =
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    sections.find((section) => section.key === activeFilter) || sections[0];
  const filteredSections = sections.filter((section) =>
    section.label.toLowerCase().includes(filterSearchQuery.toLowerCase()),
  );
  const previewKeywords = draftFilters.keywords.map(({ keyword }) => keyword);
  // snake_case (and legacy spaced) keywords match as whole phrases — worth a
  // heads-up since separate words match far more profiles.
  const compoundKeywords = previewKeywords.filter((keyword) =>
    /[_\s]/.test(keyword),
  );
  // Title and keywords match candidates on either, so they join with OR;
  // the remaining filters narrow the results with AND.
  const matchPreview = [
    draftFilters.title.length
      ? `title:(${draftFilters.title.map((title) => `"${title}"`).join(" OR ")})`
      : "",
    previewKeywords.length
      ? `keywords:(${previewKeywords.map((keyword) => `"${keyword}"`).join(" OR ")})`
      : "",
  ]
    .filter(Boolean)
    .join(" OR ");
  const sallyStatesPreview = isSallyStatesFiltered(draftSallyStates)
    ? `sallyStatus:(${SALLY_STATE_OPTIONS.filter(({ state }) =>
        draftSallyStates.includes(state),
      )
        .map(({ label }) => `"${label}"`)
        .join(" OR ")})`
    : "";
  // Sally mode only forwards keywords, so previewing the rest would promise
  // narrowing that never happens.
  const previewClauses = isSallyMode
    ? [
        sallyStatesPreview,
        previewKeywords.length
          ? `keywords:(${previewKeywords.map((keyword) => `"${keyword}"`).join(" OR ")})`
          : "",
      ]
    : [
        matchPreview.includes(" OR keywords:")
          ? `(${matchPreview})`
          : matchPreview,
        draftFilters.payCeiling ? `pay:[* TO ${draftFilters.payCeiling}]` : "",
        draftFilters.includeRejected ? "includeRejected:true" : "",
        draftFilters.includePastSubmitted ? "includePastSubmitted:true" : "",
      ];
  const queryPreview = previewClauses.filter(Boolean).join(" AND ");

  // Page breaks ask for the dialog from deep in the list via the store.
  const { filtersDialogRequestId } = searchStore();
  const handledFiltersDialogRequestIdRef = useRef(filtersDialogRequestId);
  useEffect(() => {
    if (handledFiltersDialogRequestIdRef.current === filtersDialogRequestId) {
      return;
    }
    handledFiltersDialogRequestIdRef.current = filtersDialogRequestId;
    handleOpenChange(true);
    // handleOpenChange reads current props; re-running on their change would
    // reopen the dialog.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersDialogRequestId]);

  /* Handlers */
  const handleOpenChange = (open: boolean) => {
    if (open) {
      setActiveFilter(isSallyMode ? "sallyStates" : "keywords");
      setDraftFilters(filters);
      setDraftSallyStates(sallyStates);
      setFilterSearchQuery("");
      setKeywordInput("");
      setTitleInput(filters.title.join(", "));
    }
    setIsOpen(open);
  };

  const handleApply = () => {
    onSallyStatesChange(draftSallyStates);
    // Commit any in-progress token so typing + immediately applying works.
    const pendingKeyword = keywordInput.trim();
    const keywords = pendingKeyword
      ? appendKeywords(draftFilters.keywords, [pendingKeyword])
      : draftFilters.keywords;
    setKeywordInput("");
    const nextFilters = {
      includePastSubmitted: draftFilters.includePastSubmitted,
      includeRejected: draftFilters.includeRejected,
      keywords,
      // The dialog doesn't edit location — carry the header's override through.
      location: draftFilters.location,
      payCeiling: draftFilters.payCeiling.trim(),
      title: draftFilters.title.map((title) => title.trim()).filter(Boolean),
      tenure: hasTenureRange(draftFilters.tenure) ? draftFilters.tenure : null,
    };
    const keywordsChanged =
      getKeywordInput(keywords) !== getKeywordInput(filters.keywords);

    onApply(nextFilters);
    if (!keywordsChanged || keywords.length === 0) {
      setIsOpen(false);
      return;
    }

    classifyKeywordColors(
      { keywords: keywords.map(({ keyword }) => keyword) },
      {
        onError: () => {
          setIsOpen(false);
        },
        onSuccess: (classifiedKeywords) => {
          onApply({ ...nextFilters, keywords: classifiedKeywords });
          setIsOpen(false);
        },
      },
    );
  };

  const handleFilterInputKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key === "Enter" && !isClassifyingKeywordColors) {
      event.preventDefault();
      handleApply();
    }
  };

  const handleOpenButtonClick = () => {
    handleOpenChange(true);
  };

  const handleClearAllFilters = () => {
    setDraftFilters(emptyFilters);
    setDraftSallyStates(SALLY_STATE_OPTIONS.map(({ state }) => state));
    setKeywordInput("");
    setTitleInput("");
  };

  const handleSallyStateToggle = (state: SallyState) => {
    setDraftSallyStates((currentStates) =>
      currentStates.includes(state)
        ? currentStates.filter((currentState) => currentState !== state)
        : [...currentStates, state],
    );
  };

  const handleFilterChange = (
    filter: Exclude<
      SearchV2Filter,
      | "includePastSubmitted"
      | "includeRejected"
      | "keywords"
      | "sallyStates"
      | "title"
    >,
    value: string,
  ) => {
    setDraftFilters((currentFilters) => ({
      ...currentFilters,
      [filter]: value,
    }));
  };

  const handleTitleChange = (value: string) => {
    setTitleInput(value);
    setDraftFilters((currentFilters) => ({
      ...currentFilters,
      title: [...new Set(value.split(",").map((title) => title.trim()))]
        .filter(Boolean)
        .slice(0, 5),
    }));
  };

  // Appends new single-token keywords, de-duped against what's already there.
  // Existing keywords (legacy compounds included) are left untouched.
  const appendKeywords = (
    currentKeywords: ClassifiedKeyword[],
    tokens: string[],
  ) => {
    const seenKeywords = new Set(
      currentKeywords.map(({ keyword }) => keyword.trim().toLowerCase()),
    );
    const addedKeywords: ClassifiedKeyword[] = [];
    tokens.forEach((token) => {
      const normalizedToken = token.toLowerCase();
      if (seenKeywords.has(normalizedToken)) return;
      seenKeywords.add(normalizedToken);
      addedKeywords.push({ category: null, color: null, keyword: token });
    });
    return [...currentKeywords, ...addedKeywords].slice(
      0,
      MAX_SEARCH_V2_KEYWORDS,
    );
  };

  const addKeywords = (tokens: string[]) => {
    setDraftFilters((currentFilters) => ({
      ...currentFilters,
      keywords: appendKeywords(currentFilters.keywords, tokens),
    }));
  };

  const handleKeywordChipRemove = (keyword: string) => {
    setDraftFilters((currentFilters) => ({
      ...currentFilters,
      keywords: currentFilters.keywords.filter(
        (classifiedKeyword) => classifiedKeyword.keyword !== keyword,
      ),
    }));
  };

  // Space or comma commits the token as a chip — compounds must use
  // snake_case (cnc_machinist), so spaces can never live inside a keyword.
  const handleKeywordInputChange = (value: string) => {
    if (!/[\s,]/.test(value)) {
      setKeywordInput(value);
      return;
    }
    const tokens = value.split(/[\s,]+/).filter(Boolean);
    const pendingToken = /[\s,]$/.test(value) ? "" : (tokens.pop() ?? "");
    if (tokens.length) {
      addKeywords(tokens);
    }
    setKeywordInput(pendingToken);
  };

  const handleKeywordInputKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key === "Enter") {
      event.preventDefault();
      const pendingKeyword = keywordInput.trim();
      if (pendingKeyword) {
        addKeywords([pendingKeyword]);
        setKeywordInput("");
        return;
      }
      if (!isClassifyingKeywordColors) {
        handleApply();
      }
      return;
    }
    // Backspace on an empty input removes the last chip.
    if (event.key === "Backspace" && !keywordInput) {
      setDraftFilters((currentFilters) => ({
        ...currentFilters,
        keywords: currentFilters.keywords.slice(0, -1),
      }));
    }
  };

  const handleBooleanFilterChange = (
    filter: "includePastSubmitted" | "includeRejected",
    value: boolean,
  ) => {
    setDraftFilters((currentFilters) => ({
      ...currentFilters,
      [filter]: value,
    }));
  };

  const handleFilterRemove = (filter: SearchV2Filter) => {
    if (filter === "sallyStates") {
      setDraftSallyStates(SALLY_STATE_OPTIONS.map(({ state }) => state));
      return;
    }
    if (filter === "title") {
      setDraftFilters((currentFilters) => ({
        ...currentFilters,
        title: [],
      }));
      setTitleInput("");
      return;
    }
    if (filter === "includeRejected" || filter === "includePastSubmitted") {
      handleBooleanFilterChange(filter, false);
      return;
    }
    if (filter === "keywords") {
      setKeywordInput("");
      setDraftFilters((currentFilters) => ({
        ...currentFilters,
        keywords: [],
      }));
      return;
    }
    handleFilterChange(filter, "");
  };

  /* Render */
  const renderSummaryFilters = () => {
    return sections.flatMap((section) => {
      if (!hasFilterValue(section.key)) {
        return [];
      }
      // Read each field off draftFilters rather than indexing by the key union —
      // indexing widens the value to every filter's type at once.
      const label = (() => {
        if (section.key === "sallyStates") {
          return `${String(draftSallyStates.length)} Sally ${
            draftSallyStates.length === 1 ? "status" : "statuses"
          }`;
        }
        if (section.key === "includeRejected") {
          return "Including rejected candidates";
        }
        if (section.key === "includePastSubmitted") {
          return "Including placed & past submitted";
        }
        if (section.key === "payCeiling") {
          return `$${draftFilters.payCeiling}/hr max`;
        }
        if (section.key === "keywords") {
          return draftFilters.keywords.map(({ keyword }) => keyword).join(", ");
        }
        return draftFilters.title.join(", ");
      })();
      return (
        <Button
          className="h-auto shrink-0 gap-1 whitespace-nowrap rounded-none bg-transparent px-0 py-0 text-xs font-normal text-blue-700 hover:bg-transparent hover:text-blue-700 hover:underline"
          key={section.key}
          onClick={() => {
            handleFilterRemove(section.key);
          }}
          size="sm"
          type="button"
          variant="ghost"
        >
          {label}
          <XIcon className="h-3 w-3" />
        </Button>
      );
    });
  };

  const renderSallyStatesInput = () => {
    const counts = sally.data?.counts;
    return (
      <Column className="gap-3">
        <Text className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Included statuses
        </Text>
        <Row className="flex-wrap items-center gap-2">
          {SALLY_STATE_OPTIONS.map(({ label, state }) => {
            const count = counts?.[state] ?? 0;
            const isSelected = draftSallyStates.includes(state);
            return (
              <Button
                className={cn(
                  "h-7 rounded-full border px-3 text-xs font-medium",
                  isSelected
                    ? "border-primary-200 bg-primary-50 text-primary-700 hover:bg-primary-50"
                    : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50",
                  count === 0 && "opacity-50",
                )}
                key={state}
                onClick={() => {
                  handleSallyStateToggle(state);
                }}
                type="button"
                variant="ghost"
              >
                {label} · {count}
              </Button>
            );
          })}
        </Row>
        <Text className="text-xs text-slate-500" variant="secondary">
          Counts cover everyone Sally worked on this job. Held off, no reply,
          declined, and bad contact are the ones worth a human second attempt —
          candidates often answer a person after ignoring the agent.
        </Text>
      </Column>
    );
  };

  const renderFilterInput = () => {
    if (activeSection.key === "sallyStates") {
      return renderSallyStatesInput();
    }

    if (activeSection.key === "title") {
      return (
        <Column className="gap-3">
          <Text className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Included titles
          </Text>
          <Input
            autoFocus
            onChange={(event) => {
              handleTitleChange(event.target.value);
            }}
            onKeyDown={handleFilterInputKeyDown}
            placeholder={activeSection.placeholder}
            spellCheck
            value={titleInput}
          />
          <Text className="text-xs text-slate-500" variant="secondary">
            Separate titles with commas. Candidates matching any of up to five
            titles will be included.
          </Text>
          {draftFilters.title.length > 0 ? (
            <Row className="items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2.5">
              <InfoIcon className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
              <Text className="text-xs font-normal leading-relaxed text-blue-800">
                Use titles to hone an existing search, not as a starting point.
                Titles can improve focus, but may hide qualified candidates
                whose companies used different titles for similar work.
              </Text>
            </Row>
          ) : null}
        </Column>
      );
    }

    if (activeSection.key === "payCeiling") {
      return (
        <Column className="gap-3">
          <Text className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Included
          </Text>
          <Column className="gap-1.5">
            <Text className="text-xs text-slate-500" variant="secondary">
              Maximum ($/hr)
            </Text>
            <Input
              min="0"
              onChange={(event) => {
                handleFilterChange("payCeiling", event.target.value);
              }}
              onKeyDown={handleFilterInputKeyDown}
              placeholder={activeSection.placeholder}
              type="number"
              value={draftFilters.payCeiling}
            />
          </Column>
        </Column>
      );
    }

    if (
      activeSection.key === "includeRejected" ||
      activeSection.key === "includePastSubmitted"
    ) {
      // Capture the narrowed key — the callback below loses the narrowing.
      const booleanFilter = activeSection.key;
      const isIncludeRejected = booleanFilter === "includeRejected";
      const title = isIncludeRejected
        ? "Include rejected candidates"
        : "Include placed & past-submitted candidates";
      const description = isIncludeRejected
        ? "Show candidates rejected from this job in the search results."
        : "Show candidates who are placed anywhere or past the submitted stage in an active pipeline, sorted to the bottom of each page.";
      const value = isIncludeRejected
        ? draftFilters.includeRejected
        : draftFilters.includePastSubmitted;
      return (
        <Row className="items-center justify-between gap-6 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
          <Column className="gap-0.5">
            <Text className="text-sm font-medium text-slate-800">{title}</Text>
            <Text className="text-xs font-normal text-slate-500">
              {description}
            </Text>
          </Column>
          <Switch
            checked={value}
            onCheckedChange={(nextValue) => {
              handleBooleanFilterChange(booleanFilter, nextValue);
            }}
          />
        </Row>
      );
    }

    if (activeSection.key === "tenure") {
      return (
        <TenureFilter
          onChange={(tenure) => {
            setDraftFilters((current) => ({ ...current, tenure }));
          }}
          value={draftTenure}
        />
      );
    }

    const keywordCount =
      draftFilters.keywords.length + (keywordInput.trim() ? 1 : 0);
    return (
      <Column className="gap-3">
        <Text className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Included
        </Text>
        {draftFilters.keywords.length > 0 ? (
          <Row className="flex-wrap gap-1.5">
            {draftFilters.keywords.map(({ keyword }) => (
              <Row
                className="h-7 items-center gap-1 rounded-md bg-slate-100 pl-2.5 pr-1"
                key={keyword}
              >
                <Text className="text-xs font-normal text-slate-600">
                  {keyword}
                </Text>
                <Button
                  className="h-6 w-6 p-0 text-slate-400 hover:text-slate-700"
                  onClick={() => {
                    handleKeywordChipRemove(keyword);
                  }}
                  size="icon"
                  type="button"
                  variant="ghost"
                >
                  <XIcon className="h-3.5 w-3.5" />
                </Button>
              </Row>
            ))}
          </Row>
        ) : null}
        <Input
          autoFocus
          onChange={(event) => {
            handleKeywordInputChange(event.target.value);
          }}
          onKeyDown={handleKeywordInputKeyDown}
          placeholder={activeSection.placeholder}
          spellCheck
          value={keywordInput}
        />
        {compoundKeywords.length > 0 ? (
          <Row className="items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2.5">
            <InfoIcon className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
            <Column className="gap-1">
              <Text className="text-xs font-medium text-blue-800">
                Compound keywords match the whole phrase only
              </Text>
              <Text className="text-xs font-normal leading-relaxed text-blue-800">
                {compoundKeywords.map((keyword) => `“${keyword}”`).join(", ")}{" "}
                will miss candidates whose experience uses those words
                separately. Prefer individual keywords unless the words are
                meaningless apart (like a brand name).
              </Text>
            </Column>
          </Row>
        ) : null}
        {keywordCount >= 6 ? (
          <Row className="items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2.5">
            <InfoIcon className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
            <Text className="text-xs font-normal leading-relaxed text-blue-800">
              More than six keywords can weaken the ranking signal and make
              later result pages noisier. Start focused, then add terms as you
              review results.
            </Text>
          </Row>
        ) : null}
        {keywordCount >= MAX_SEARCH_V2_KEYWORDS ? (
          <Text className="text-xs font-medium text-amber-600">
            Keyword limit reached — only the first {MAX_SEARCH_V2_KEYWORDS}{" "}
            keywords are used.
          </Text>
        ) : (
          <Text className="text-xs text-slate-500" variant="secondary">
            Press space or enter to add a keyword. For multi-word terms use
            snake_case (e.g. cnc_machinist). Candidates matching any keyword are
            included. Up to {MAX_SEARCH_V2_KEYWORDS} keywords.
          </Text>
        )}
      </Column>
    );
  };

  const render = () => {
    const ActiveFilterIcon = activeSection.icon;
    return (
      <Dialog onOpenChange={handleOpenChange} open={isOpen}>
        <Button
          className="relative h-12 shrink-0 gap-2 rounded-[18px] border-slate-200 bg-slate-50 px-4 text-sm text-slate-600 hover:bg-slate-100 hover:text-slate-700"
          onClick={handleOpenButtonClick}
          type="button"
          variant="outline"
        >
          <ListFilterIcon className="h-4 w-4" />
          Filters
          {appliedFilterCount > 0 && (
            <Row className="absolute -right-1 -top-1 h-4 min-w-4 items-center justify-center rounded-full bg-blue-600 px-1">
              <Text className="text-[9px] font-semibold leading-none text-white">
                {appliedFilterCount}
              </Text>
            </Row>
          )}
        </Button>
        <DialogContent className="h-[min(680px,92vh)] max-w-[980px] grid-rows-[auto_minmax(0,1fr)_auto] gap-0 overflow-hidden p-0">
          <DialogHeader className="h-fit min-w-0 border-b border-slate-200 px-7 py-5">
            <Row className="min-w-0 items-center gap-3 pr-8">
              <DialogTitle className="shrink-0 text-lg">
                Edit Search Filters
              </DialogTitle>
              <Text className="shrink-0 rounded-full bg-slate-100 px-2 py-1 text-xs text-slate-600">
                {activeFilterCount}{" "}
                {activeFilterCount === 1 ? "filter" : "filters"}
              </Text>
              <Row className="min-w-0 flex-1 flex-nowrap items-center gap-x-3 overflow-x-auto">
                {renderSummaryFilters()}
              </Row>
              {activeFilterCount ? (
                <Button
                  className="h-8 shrink-0 px-2 text-xs font-normal text-slate-500 hover:text-red-600"
                  onClick={handleClearAllFilters}
                  size="sm"
                  type="button"
                  variant="ghost"
                >
                  Clear all filters
                </Button>
              ) : null}
              {hiddenUserCount > 0 ? (
                <Button
                  className="h-8 shrink-0 gap-1.5 px-2 text-xs font-normal text-slate-500 hover:text-slate-800"
                  onClick={onClearHiddenUsers}
                  size="sm"
                  type="button"
                  variant="ghost"
                >
                  <EyeIcon className="h-3.5 w-3.5" />
                  Show {hiddenUserCount} hidden
                </Button>
              ) : null}
            </Row>
          </DialogHeader>

          <Row className="min-h-0 min-w-0 items-stretch">
            <Column className="w-[280px] shrink-0 gap-5 overflow-y-auto border-r border-slate-200 bg-slate-50/60 p-5">
              <Row className="relative items-center">
                <SearchIcon className="absolute left-3 h-4 w-4 text-slate-400" />
                <Input
                  className="pl-9"
                  onChange={(event) => {
                    setFilterSearchQuery(event.target.value);
                  }}
                  placeholder="Search filters"
                  value={filterSearchQuery}
                />
              </Row>
              <Column className="gap-1">
                {filteredSections.map((section) => {
                  const FilterIcon = section.icon;
                  return (
                    <Button
                      className={cn(
                        "w-full justify-start px-3",
                        activeFilter === section.key &&
                          "bg-blue-50 text-blue-700 hover:bg-blue-50 hover:text-blue-700",
                      )}
                      key={section.key}
                      onClick={() => {
                        setActiveFilter(section.key);
                      }}
                      type="button"
                      variant="ghost"
                    >
                      <FilterIcon />
                      {section.label}
                      {hasFilterValue(section.key) ? (
                        <Row className="ml-auto h-2 w-2 rounded-full bg-blue-500" />
                      ) : null}
                    </Button>
                  );
                })}
              </Column>
            </Column>

            <Column className="min-w-0 flex-1 overflow-y-auto p-8">
              <Row className="mb-1 items-center gap-2">
                <ActiveFilterIcon className="h-6 w-6 text-slate-700" />
                <Text className="text-2xl font-semibold text-slate-900">
                  {activeSection.label}
                </Text>
              </Row>
              <Text className="mb-7 text-slate-500" variant="secondary">
                {activeSection.description}
              </Text>
              {renderFilterInput()}
            </Column>
          </Row>

          <Column className="min-w-0 gap-3 border-t border-slate-200 bg-white px-6 py-4">
            <Row className="min-w-0 items-center gap-3">
              <Text className="shrink-0 text-xs font-semibold uppercase tracking-wide text-slate-500">
                Query preview
              </Text>
              <Text className="flex h-9 min-w-0 flex-1 items-center overflow-x-auto whitespace-nowrap rounded-md bg-slate-50 px-3 font-mono text-xs text-slate-700">
                {queryPreview || "No query yet"}
              </Text>
              <Button
                className="h-9 shrink-0"
                disabled={isClassifyingKeywordColors}
                onClick={handleApply}
                size="sm"
                type="button"
              >
                {isClassifyingKeywordColors ? (
                  <LoaderCircleIcon className="animate-spin" />
                ) : (
                  <CheckIcon />
                )}
                {isClassifyingKeywordColors
                  ? "Classifying keywords"
                  : "Save and Search"}
              </Button>
            </Row>
          </Column>
        </DialogContent>
      </Dialog>
    );
  };

  return render();
};
